import { useRuntimeConfig } from '#app';
import { ga4ConversionValue } from '../utils/ga4-conversion-value';

// Thin wrapper that fires a named event to GA4, Yandex Metrika, AND Meta Pixel.
// No-ops on server and in dev (plugins skip script injection there, so
// window.gtag / window.ym / window.fbq aren't defined).

export type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;

// Maps our domain event names to Meta standard events with normalized
// custom_data parameters. Returning null means the event is not relevant
// for Meta (e.g. internal UI counters like locale_change, map_open_click).
//
// Event-name standards we use:
// - `Lead`             — completed contact / booking-request submission
// - `InitiateCheckout` — opened the booking-request form (intent stronger than view)
// - `Contact`          — clicked WhatsApp/Telegram/Email CTA (any cold contact intent)
// - `Search`           — submitted the search form on the home/hero
//
// Custom-data shape follows Meta's catalog conventions for `lodging`:
// content_ids = [String(listing_id)], content_type = 'lodging', value/currency
// for monetary events, num_items = nights, content_category = secondary tag.
function mapToMetaEvent(
  name: string,
  params: Record<string, string | number | boolean>,
): { event: string; data: Record<string, unknown> } | null {
  const listingId = params.listing_id != null ? String(params.listing_id) : undefined;
  const totalUsd = typeof params.total_usd === 'number' ? params.total_usd : undefined;
  const nights = typeof params.nights === 'number' ? params.nights : undefined;

  switch (name) {
    case 'booking_request_submit':
      return {
        event: 'Lead',
        data: {
          ...(totalUsd != null ? { value: totalUsd, currency: 'USD' } : {}),
          ...(listingId ? { content_ids: [listingId], content_type: 'lodging' } : {}),
          ...(nights != null ? { num_items: nights } : {}),
        },
      };

    case 'booking_request_open':
      return {
        event: 'InitiateCheckout',
        data: {
          ...(totalUsd != null ? { value: totalUsd, currency: 'USD' } : {}),
          ...(listingId ? { content_ids: [listingId], content_type: 'lodging' } : {}),
          ...(nights != null ? { num_items: nights } : {}),
        },
      };

    case 'booking_cta_click':
      return {
        event: 'Contact',
        data: {
          ...(listingId ? { content_ids: [listingId], content_type: 'lodging' } : {}),
          content_category: String(params.channel ?? 'cta'),
        },
      };

    case 'ask_question_click':
      return {
        event: 'Contact',
        data: {
          ...(listingId ? { content_ids: [listingId], content_type: 'lodging' } : {}),
          content_category: 'question',
          content_name: String(params.channel ?? ''),
        },
      };

    case 'contact_form_submit':
      return {
        event: 'Lead',
        data: {
          content_category: String(params.placement ?? 'contact'),
        },
      };

    case 'prices_calculator_long_stay_contact':
      return {
        event: 'Lead',
        data: { content_category: 'long_stay' },
      };

    case 'hero_search_submit':
    case 'search_submit':
      return {
        event: 'Search',
        data: {
          search_string: `${params.from ?? ''}_${params.to ?? ''}_${params.guests ?? ''}`,
          content_category: 'apartment',
        },
      };

    case 'booking_widget_click':
      return {
        event: 'Contact',
        data: {
          content_category: 'sticky_bar',
          content_name: String(params.channel ?? ''),
        },
      };

    case 'apartment_view':
      return {
        event: 'ViewContent',
        data: {
          ...(listingId ? { content_ids: [listingId], content_type: 'lodging' } : {}),
          ...(params.daily_rate_usd != null
            ? { value: Number(params.daily_rate_usd), currency: 'USD' }
            : {}),
          ...(params.apartment_title ? { content_name: String(params.apartment_title) } : {}),
        },
      };

    default:
      return null;
  }
}

// Cheap, non-cryptographic event_id for client/server CAPI dedup. Future
// CAPI plumbing in server/services/meta-capi.ts will receive this same
// id from the client payload and pass it as `event_id`, letting Meta
// dedupe browser+server signals automatically.
function newEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function useAnalytics() {
  const { yandexMetrikaId, yandexMetrikaIdRu } = useRuntimeConfig().public as {
    yandexMetrikaId?: string;
    yandexMetrikaIdRu?: string;
  };

  function trackEvent(name: string, params: AnalyticsParams = {}) {
    if (!import.meta.client) return;

    // Clean up null/undefined — analytics providers dislike them.
    const cleaned: Record<string, string | number | boolean> = {};
    for (const [k, v] of Object.entries(params)) {
      if (v != null) cleaned[k] = v;
    }

    if (typeof window.gtag === 'function') {
      // Inject the GA4 reserved `value`/`currency` so conversions carry a
      // monetary weight (GA4-only — YM and Meta derive value their own way).
      const gv = ga4ConversionValue(name, cleaned);
      window.gtag('event', name, gv ? { ...cleaned, ...gv } : cleaned);
    }
    // Match the property the plugin initialized for this domain.
    const activeYmId = location.hostname.endsWith('georgiadream.ru')
      ? yandexMetrikaIdRu
      : yandexMetrikaId;
    if (activeYmId && typeof window.ym === 'function') {
      window.ym(Number(activeYmId), 'reachGoal', name, cleaned);
    }

    // Meta Pixel sink — only fires for events that have a valid mapping.
    // If the caller passed `meta_event_id` in params (form submit handler
    // that also sent the same id to /api/contact for CAPI mirror), use it
    // as fbq eventID — Meta then dedupes browser+server signals
    // automatically. Otherwise generate a fresh id (browser-only event).
    if (typeof window.fbq === 'function') {
      const meta = mapToMetaEvent(name, cleaned);
      if (meta) {
        const eventId =
          typeof cleaned.meta_event_id === 'string' && cleaned.meta_event_id.length > 0
            ? cleaned.meta_event_id
            : newEventId();
        window.fbq('track', meta.event, meta.data, { eventID: eventId });
      }
    }
  }

  return { trackEvent };
}
